import Layout from 'containers/Layout'
import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import Container from 'theme/atoms/Container'
import Summary from './Summary/Summary'
import CartItems from './CartItems'
import EmptyCart from './EmptyCart'
import InfoBox from './InfoBox'
import useShareCart from 'hooks/account/cart/useShareCart'
import * as evt from './event'
import Spinner from '../../atoms/Spinner'
import ErrorModal from '../../molecules/account/CartSummary/ShareCart/ErrorModal'
import useTranslations from 'hooks/useTranslations'
import withTranslations from './withTranslations'
import Seo from 'theme/atoms/SEO'
import config from 'config'
import ChannelInfoModal from './ChannelInfoModal'
import useAccount from 'hooks/account/useAccount'
import { isItemChannelRestricted } from 'hooks/account/utils'
import DyProductSlider from 'theme/molecules/DyProductSlider'
import DeliveryByPartnerModal from '../../molecules/DeliveryByPartnerModal'
import PaypalIcon from 'assets/paymentIcons/paypal.svg'
import { CartDeleteContextProvider } from 'hooks/account/useAccount/cart/useCartContext'

type Props = {
  location: {
    pathname: string
    origin: string
  }
}

export function Cart(props: Props) {
  const t = useTranslations<'templates-Cart'>()
  const account = useAccount()
  const cart = account.cart
  const shareCart = useShareCart()
  const [showDeliveryByPartnerModal, setShowDeliveryByPartnerModal] =
    React.useState(false)

  const cartRestricted = React.useMemo(() => {
    return (
      cart.data.items.findIndex((item) =>
        isItemChannelRestricted(item, account.channel)
      ) !== -1
    )
  }, [account.channel, cart.data.items])

  React.useEffect(() => {
    if (cart.setup && cart.data) evt.cartPageView(cart.data)
  }, [cart.setup, cart.data])

  if (!cart.setup || !shareCart.setup || shareCart.setupFetch)
    return (
      <Layout
        data-cy-ctx="templates/Cart"
        pageContext={{
          type: 'Cart',
          ready: false,
          skus: []
        }}
      >
        <Seo
          canonicalUrl={`${config.baseUrl}${props.location.pathname}`}
          location={props.location}
          title={t.asText(false, 'seo_title')}
          description=""
          type="website"
          seoNoIndex={true}
          seoNoFollow={true}
        />
        <LoadingSpinner
          style={{ height: 500 }}
          data-cy-state="loading"
          className="spinner"
        >
          <Spinner size="medium" />
        </LoadingSpinner>
      </Layout>
    )

  if (cart.data.items.length === 0)
    return (
      <Layout
        data-cy-ctx="templates/Cart"
        pageContext={{
          type: 'Cart',
          ready: true,
          skus: []
        }}
      >
        <Seo
          canonicalUrl={`${config.baseUrl}${props.location.pathname}`}
          location={props.location}
          title={t.asText(false, 'seo_title')}
          description=""
          type="website"
          seoNoIndex={true}
          seoNoFollow={true}
        />
        <Container>
          <Wrapper className="Cart" data-cy-state="show-empty-cart">
            <ErrorModal error={shareCart.error} />
            <EmptyCart />
          </Wrapper>
        </Container>
      </Layout>
    )

  return (
    <Layout
      data-cy-ctx="templates/Cart"
      pageContext={{
        type: 'Cart',
        ready: true,
        skus: cart.data.items.map((item) => item.sku)
      }}
    >
      <Seo
        canonicalUrl={`${config.baseUrl}${props.location.pathname}`}
        location={props.location}
        title={t.asText(false, 'seo_title')}
        description=""
        type="website"
      />
      <Container>
        <CartDeleteContextProvider>
          <Wrapper className="Cart">
            <h1>{t('your_cart')}</h1>

            <div className="content">
              {shareCart.error && (
                <ErrorModal
                  error={shareCart.error}
                  data-cy-state="show-error-modal"
                />
              )}
              <div className="inner-content">
                {cartRestricted && (
                  <ChannelInfoModal data-cy-state="show-channel-info-modal" />
                )}
                {config.features.paypalInstallment &&
                  cart.data.totalPrice > 30 &&
                  cart.data.totalPrice < 2000 && (
                    <InfoBox
                      data-cy-state="show-paypal-later-modal"
                      description={t('installment_payment', {
                        PAYPAL: '-',
                        __transformers: {
                          PAYPAL: () => (
                            <IconContainer>
                              <PaypalIcon />
                            </IconContainer>
                          )
                        }
                      })}
                    />
                  )}
                {config.features.showCartInfoboxFrom &&
                  config.features.showCartInfoboxTo &&
                  new Date(config.features.showCartInfoboxFrom) <= new Date() &&
                  new Date(config.features.showCartInfoboxTo) >= new Date() && (
                    <InfoBox
                      data-cy-state="show-cart-infobox"
                      description={t(`info_box`)}
                    />
                  )}
                <CartItems
                  onPartnerLinkClick={() => setShowDeliveryByPartnerModal(true)}
                />
              </div>
              <Summary isRestricted={cartRestricted} />
            </div>
            <div className="product-sliders">
              <DyProductSlider
                dySelector="wdv-1573-recommendation-cart-slot1"
                title="Recommended Products"
                gridArea="zeroresults-dy-slider"
              />
              <DyProductSlider
                dySelector="wdv-2842-recommendation-cart-slot2"
                title="Recommended Products 2"
                gridArea="zeroresults-dy-slider-2"
              />
            </div>
            {showDeliveryByPartnerModal && (
              <DeliveryByPartnerModal
                data-cy-state="show-delivery-by-partner-modal"
                onClose={() => setShowDeliveryByPartnerModal(false)}
              />
            )}
          </Wrapper>
        </CartDeleteContextProvider>
      </Container>
    </Layout>
  )
}

export default withTranslations(Cart)

const Wrapper = styled.div`
  padding: ${theme.spacing('m')} 0;

  @media (min-width: ${theme.ms.MD}px) {
    padding: ${theme.spacing('l')} 0;
  }

  > h1 {
    margin-bottom: ${theme.spacing('m')};
    ${theme.ty('rc-3xl', 'bold')}
    @media (min-width: ${theme.ms.MD}px) {
      ${theme.ty('rc-4xl', 'bold')}
    }
  }
  > .content {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing('l')};
    @media (min-width: ${theme.ms.LG}px) {
      flex-direction: row;
      gap: ${theme.spacing('m')};
      > .Summary {
        width: 420px;
      }
    }

    > .inner-content {
      flex: 1;
      > .ChannelInfoModal {
        margin-bottom: ${theme.spacing('m')};
      }
    }
  }
  > .product-sliders {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing('m')};
    @media (min-width: ${theme.ms.LG}px) {
      gap: ${theme.spacing('l')};
    }

    > .dy-slider {
      height: 31.25rem;
      width: 31.25rem;
      background-color: red;
    }
  }
  > .EmptyCart {
    max-width: 40rem;
    margin: 0 auto;
  }
`

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const IconContainer = styled.div`
  width: ${theme.spacing('xl')};
  margin-left: ${theme.spacing('xxs')};
  display: flex;
  align-items: center;
  height: 100%;
  > svg {
    width: 100%;
  }
`
